import * as React from 'react'
import styled, { css } from 'styled-components'

import * as copy from './copy'
import * as bp from '../constants/breakpoints'
import * as colors from '../constants/colors'
import * as fonts from '../constants/fonts'
import { Icon } from '../icons/glyphs'

// TODO: rename colors with new design system names
export type BackgroundColor =
  | 'blue'
  | 'blue-hover'
  | 'light-blue'
  | 'dark-blue'
  | 'dark-green'
  | 'light-green'
  | 'green'
  | 'grey'
  | 'light-grey'
  | 'indigo'
  | 'red'
  | 'light-red'
  | 'yellow'
  | 'sand'
export type Size = 'small' | 'big'

type TagProps = {
  size?: Size
  backgroundColor?: BackgroundColor
  textColor?: string
} & React.HTMLAttributes<HTMLDivElement>

export const Tag = styled.div<TagProps>`
  background-color: ${colors.PRIMARY_SAND_200};
  border-radius: 2px;
  color: ${colors.TEXT_SECONDARY};
  display: inline-block;
  font-family: ${fonts.PRIMARY_FONT};
  font-size: 10px;
  font-weight: bold;
  height: 20px;
  line-height: 20px;
  padding: 0em 0.6em;
  text-transform: uppercase;
  width: max-content;

  /* Color */
  ${(props) => {
    if (props.theme?.colors?.primary) {
      return css`
        color: ${colors.PRIMARY_GREY_000};
        background-color: ${props.theme.colors.primary};
      `
    }
    if (props.backgroundColor === 'blue') {
      return css`
        color: ${colors.PRIMARY_GREY_000};
        background-color: ${colors.PRIMARY_BLUE_500};
      `
    }
    if (props.backgroundColor === 'blue-hover') {
      return css`
        color: ${colors.PRIMARY_GREY_000};
        background-color: ${colors.PRIMARY_BLUE_700};
      `
    }
    if (props.backgroundColor === 'light-blue') {
      return css`
        color: ${colors.PRIMARY_BLUE_900};
        background-color: ${colors.PRIMARY_BLUE_100};
      `
    }
    if (props.backgroundColor === 'dark-blue') {
      return css`
        color: ${colors.PRIMARY_GREY_000};
        background-color: ${colors.PRIMARY_BLUE_900};
      `
    }
    if (props.backgroundColor === 'dark-green') {
      return css`
        color: ${colors.PRIMARY_GREY_000};
        background-color: ${colors.SECONDARY_GREEN_800};
      `
    }
    if (props.backgroundColor === 'green') {
      return css`
        color: ${colors.PRIMARY_GREY_000};
        background-color: ${colors.SECONDARY_GREEN_500};
      `
    }
    if (props.backgroundColor === 'light-green') {
      return css`
        color: ${colors.SECONDARY_GREEN_800};
        background-color: ${colors.BACKGROUND_SUCCESS};
      `
    }
    if (props.backgroundColor === 'grey') {
      return css`
        color: ${colors.TEXT_SECONDARY};
        background-color: ${colors.PRIMARY_SAND_100};
      `
    }
    if (props.backgroundColor === 'indigo') {
      return css`
        color: ${colors.PRIMARY_GREY_000};
        background-color: ${colors.BRAND_INDIGO};
      `
    }
    if (props.backgroundColor === 'red') {
      return css`
        color: ${colors.PRIMARY_GREY_000};
        background-color: ${colors.PRIMARY_RED_900};
      `
    }
    if (props.backgroundColor === 'light-red') {
      return css`
        color: ${colors.PRIMARY_RED_700};
        background-color: ${colors.SECONDARY_RED_200};
      `
    }
    if (props.backgroundColor === 'yellow') {
      return css`
        color: ${colors.PRIMARY_GREY_000};
        background-color: ${colors.SECONDARY_YELLOW_500};
      `
    }
    if (props.backgroundColor === 'sand') {
      return css`
        color: ${colors.TEXT_TERTIARY};
        background-color: ${colors.PRIMARY_SAND_100};
      `
    }
    if (props.backgroundColor === 'light-grey') {
      return css`
        color: ${colors.PRIMARY_GREY_000};
        background-color: ${colors.PRIMARY_GREY_500};
      `
    }
  }};

  /* Size */
  ${(props) => {
    if (props.size === 'small') {
      return css`
        font-size: 8px;
        font-weight: 600;
      `
    }
    if (props.size === 'big') {
      return css`
        font-size: 15px;
      `
    }
  }};

  /* TextColor */
  ${(props) => {
    if (props.textColor) {
      return css`
        color: ${props.textColor};
      `
    }
  }};

  /* Tag with icon */
  span[role=img] {
    margin-right: 6px;
  }

  ${Icon} {
    margin-right: 6px;
  }
`
Tag.displayName = 'S.tag.Tag'

export type StatusColor = 'blue' | 'green' | 'grey' | 'red' | 'yellow'

type StatusTagProps = {
  color?: StatusColor
} & React.HTMLAttributes<HTMLDivElement>

export const StatusTag = styled.div<StatusTagProps>`
  ${copy._xSmallStyle};
  border-radius: 4px;
  font-weight: normal;
  min-height: 24px;
  line-height: 24px;
  padding: 0 6px;
  text-transform: none;
  width: max-content;

  ${Icon} {
    position: relative;
    top: -2px;
  }

  @media screen and ${bp.TABLET} {
    line-height: 20px;
    min-height: 20px;
  }

  /* Color */
  ${(props) => {
    if (props.color === 'green') {
      return css`
        color: ${colors.SECONDARY_GREEN_800};
        background-color: ${colors.BACKGROUND_SUCCESS};

        ${Icon} {
          color: ${colors.SECONDARY_GREEN_800};
        }
      `
    }
    if (props.color === 'grey') {
      return css`
        color: ${colors.TEXT_SECONDARY};
        background-color: ${colors.PRIMARY_SAND_100};

        ${Icon} {
          color: ${colors.TEXT_SECONDARY};
        }
      `
    }
    if (props.color === 'red') {
      return css`
        color: ${colors.PRIMARY_RED_700};
        background-color: ${colors.SECONDARY_RED_200};

        ${Icon} {
          color: ${colors.PRIMARY_RED_700};
        }
      `
    }
    if (props.color === 'yellow') {
      return css`
        color: ${colors.SECONDARY_YELLOW_800};
        background-color: ${colors.BACKGROUND_ALERT};

        ${Icon} {
          color: ${colors.SECONDARY_YELLOW_800};
        }
      `
    }
    if (props.theme?.colors?.primary && props.theme?.colors?.background) {
      return css`
        color: ${props.theme.colors.primary};
        background-color: ${props.theme.colors.background};
      `
    }

    return css`
      color: ${colors.PRIMARY_BLUE_900};
      background-color: ${colors.PRIMARY_BLUE_100};

      ${Icon} {
        color: ${colors.PRIMARY_BLUE_900};
      }
    `
  }};
`
StatusTag.displayName = 'S.tag.StatusTag'

export const RoundTag = styled.button`
  border: 1px solid ${colors.PRIMARY_SAND_200};
  background-color: ${colors.PRIMARY_GREY_000};
  border-radius: 16px;
  display: inline-flex;
  padding: 4px 16px 4px 12px;

  &::-moz-focus-inner {
    border: 0;
  }

  &:focus,
  &:active {
    outline: none;
  }

  ${Icon} {
    color: ${colors.PRIMARY_SAND_500};
    margin-right: 7px;
    margin-top: 3px;
  }
`
RoundTag.displayName = 'S.tag.RoundTag'

export const StatusFlag = styled.div`
  align-items: center;
  background-color: ${colors.PRIMARY_GREY_000};
  border-radius: 40px;
  display: flex;
  justify-content: center;
  padding: 3px 8px;
  width: fit-content;

  ${copy.XS} {
    top: -1px;
    position: relative;
  }
`
StatusFlag.displayName = 'S.tag.StatusFlag'

export type StatusPastilleColor = 'green' | 'blue' | 'grey' | 'red'
export const StatusPastille = styled.span<{ color: StatusPastilleColor }>`
  background-color: ${({ color }) => {
    switch (color) {
      case 'green':
        return colors.SECONDARY_GREEN_500
      case 'blue':
        return colors.PRIMARY_BLUE_500
      case 'grey':
        return colors.PRIMARY_SAND_500
      case 'red':
        return colors.PRIMARY_RED_500
      default:
    }
  }};

  border-radius: 100%;
  height: 9px;
  margin-right: 6px;
  width: 9px;
`
